import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useGeo } from '../contexts/GeoContext';
import InternationalShippingNotice from '../components/InternationalShippingNotice';
import { toast } from 'sonner';
import { thumb as optThumb } from '../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, fetchCart } = useCart();
  const { formatPrice } = useGeo();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    }
  }, [user, navigate]);

  const handleCheckout = async () => {
    if (!cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(
        `${API}/checkout/create-session`,
        { origin_url: originUrl },
        { withCredentials: true }
      );

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#525252] hover:text-[#0A0A0A]">Home</Link>
            <span className="text-[#525252]">/</span>
            <span className="font-semibold">Checkout</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-4xl font-black tracking-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
            CHECKOUT
          </h1>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Order Summary */}
            <div className="border-2 border-[#0A0A0A] bg-white">
              <div className="p-6 border-b-2 border-[#0A0A0A]">
                <h2 className="text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Order Summary
                </h2>
              </div>
              <div className="p-6">
                {cart.items?.length === 0 ? (
                  <p className="text-[#525252]">Your cart is empty</p>
                ) : (
                  <div className="space-y-4">
                    {cart.items?.map((item, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="w-16 h-20 bg-[#F5F5F5] border-2 border-[#0A0A0A] overflow-hidden flex-shrink-0">
                          {item.product?.images?.[0] && (
                            <img
                              src={optThumb(item.product.images[0])}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{item.product?.name}</h4>
                          <p className="text-xs text-[#525252] mt-1">
                            {item.size && `Size: ${item.size}`}
                            {item.size && item.color && ' | '}
                            {item.color && `Color: ${item.color}`}
                          </p>
                          <p className="text-xs text-[#525252]">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-semibold">{formatPrice(item.item_total || 0)}</p>
                      </div>
                    ))}

                    <div className="border-t-2 border-[#0A0A0A] pt-4 mt-4 space-y-3">
                      <InternationalShippingNotice variant="inline" />
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Total</span>
                        <span className="text-2xl font-bold">{formatPrice(cart.total || 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Section */}
            <div className="border-2 border-[#0A0A0A] bg-white">
              <div className="p-6 border-b-2 border-[#0A0A0A]">
                <h2 className="text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Payment
                </h2>
              </div>
              <div className="p-6">
                <p className="text-[#525252] mb-6">
                  You'll be redirected to Stripe's secure checkout to complete your payment.
                </p>
                <div className="bg-[#FDF0D5] border-2 border-[#0A0A0A] p-4 mb-6">
                  <h4 className="font-semibold mb-2">Shipping Information</h4>
                  <p className="text-sm text-[#525252]">
                    {user.name}<br />
                    {user.email}
                  </p>
                </div>
                <Button
                  onClick={handleCheckout}
                  disabled={loading || cart.items?.length === 0}
                  data-testid="proceed-to-payment-btn"
                  className="w-full bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none h-14 font-semibold text-base hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Pay ${formatPrice(cart.total || 0)}`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Checkout Success Page Component
export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/checkout/status/${sessionId}`, {
            withCredentials: true
          });

          if (data.payment_status === 'paid') {
            setStatus('success');
            setPaymentDetails(data);
            await fetchCart(); // Refresh cart (should be empty now)
            return;
          } else if (data.status === 'expired') {
            setStatus('error');
            return;
          }

          // Continue polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus('error');
          }
        } catch (err) {
          console.error('Error checking payment status:', err);
          setStatus('error');
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [fetchCart]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-lg font-semibold">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-[#FAD4D4] rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-black tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            PAYMENT FAILED
          </h1>
          <p className="text-[#525252] mb-8">
            Something went wrong with your payment. Please try again or contact support if the issue persists.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/checkout">
              <Button className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none px-8 py-3 font-semibold">
                Try Again
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-2 border-[#0A0A0A] rounded-none px-8 py-3 font-semibold">
                Go Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        <div className="w-20 h-20 bg-[#D5FDD8] rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-black tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          ORDER CONFIRMED!
        </h1>
        <p className="text-[#525252] mb-2">
          Thank you for your purchase, {user?.name || 'friend'}!
        </p>
        <p className="text-[#525252] mb-8">
          We'll send you an email confirmation shortly.
        </p>
        {paymentDetails && (
          <div className="bg-white border-2 border-[#0A0A0A] p-6 mb-8 text-left">
            <p className="text-sm text-[#525252]">Order Total</p>
            <p className="text-2xl font-bold">
              ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
            </p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/orders">
            <Button className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none px-8 py-3 font-semibold">
              View Orders
            </Button>
          </Link>
          <Link to="/products">
            <Button variant="outline" className="border-2 border-[#0A0A0A] rounded-none px-8 py-3 font-semibold">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
